import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Send, X, Phone } from "lucide-react";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled", "no_show"];
const STATUS_LABEL = { pending: "Pending", confirmed: "Confirmed", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled", no_show: "No-show" };
const STATUS_CHIP = {
  pending: "border-yellow-700 bg-yellow-950/40 text-yellow-300",
  confirmed: "border-blue-700 bg-blue-950/40 text-blue-300",
  in_progress: "border-orange-600 bg-orange-950/40 text-orange-300",
  completed: "border-green-700 bg-green-950/40 text-green-300",
  cancelled: "border-zinc-700 bg-zinc-900 text-zinc-500",
  no_show: "border-red-800 bg-red-950/40 text-red-300",
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function MonthCalendar({ appts, services, onNotify, onStatus }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null);
  const [dayModal, setDayModal] = useState(null); // ISO date string
  const nav = useNavigate();

  const byDay = useMemo(() => {
    const map = {};
    appts.forEach((a) => {
      if (!a.preferred_date) return;
      (map[a.preferred_date] = map[a.preferred_date] || []).push(a);
    });
    Object.values(map).forEach((l) => l.sort((x, y) => (x.preferred_time || "").localeCompare(y.preferred_time || "")));
    return map;
  }, [appts]);

  const cells = useMemo(() => {
    const startIdx = (cursor.getDay() + 6) % 7; // Monday-start
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startIdx; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (arr.length % 7) arr.push(null);
    return arr;
  }, [cursor]);

  const todayIso = fmtISO(new Date());
  const titleOne = (id) => services.find((s) => s.id === id)?.title || id;
  const titleFor = (a) => {
    if (a && typeof a === "object") {
      const ids = a.services?.length ? a.services : [a.service];
      return ids.map(titleOne).join(" + ");
    }
    return titleOne(a);
  };
  const shiftMonth = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));

  const dayClick = (iso) => {
    if (iso >= todayIso) nav(`/book?date=${iso}`);
  };

  return (
    <div data-testid="admin-month-calendar" className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Month view</div>
          <h3 className="mt-1 font-display text-2xl uppercase tracking-tight text-white" data-testid="month-label">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </h3>
          <p className="text-xs text-zinc-500 mt-1">All bookings, colour-coded by status. Click a booking for details — click an empty day to start a booking on that date.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="month-prev" onClick={() => shiftMonth(-1)} className="px-3 py-2 border border-zinc-800 text-white text-[11px] font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red inline-flex items-center gap-1"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
          <button data-testid="month-today" onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }} className="px-3 py-2 border border-zinc-800 text-white text-[11px] font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red">Today</button>
          <button data-testid="month-next" onClick={() => shiftMonth(1)} className="px-3 py-2 border border-zinc-800 text-white text-[11px] font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red inline-flex items-center gap-1">Next <ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {STATUSES.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <span className={`h-2.5 w-2.5 border ${STATUS_CHIP[s]}`} /> {STATUS_LABEL[s]}
          </span>
        ))}
      </div>

      <div className="border border-zinc-900">
        <div className="grid grid-cols-7 gap-px bg-zinc-900 border-b border-zinc-900">
          {WEEKDAYS.map((w) => (
            <div key={w} className="bg-zinc-950 px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-center">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-zinc-900">
          {cells.map((d, i) => {
            if (!d) return <div key={`x${i}`} className="bg-zinc-950/40 min-h-[110px]" />;
            const iso = fmtISO(d);
            const list = byDay[iso] || [];
            const isToday = iso === todayIso;
            const shown = list.slice(0, 3);
            return (
              <div
                key={iso}
                data-testid={`mcal-day-${iso}`}
                onClick={() => dayClick(iso)}
                className={`bg-zinc-950 min-h-[110px] p-1.5 flex flex-col ${isToday ? "ring-1 ring-brand-red ring-inset" : ""} ${iso >= todayIso ? "cursor-pointer hover:bg-zinc-900/50" : ""}`}
                title={iso >= todayIso ? "Click to start a booking on this date" : undefined}
              >
                <div className="flex items-center justify-between px-0.5">
                  <span className={`font-display text-base ${isToday ? "text-brand-red" : "text-white"}`}>{d.getDate()}</span>
                  {list.length > 0 && (
                    <span data-testid={`mcal-count-${iso}`} className="px-1.5 py-0.5 bg-brand-red/15 border border-brand-red/40 text-brand-red font-mono text-[9px]">{list.length}</span>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {shown.map((a) => (
                    <button
                      key={a.id}
                      data-testid={`mcal-appt-${a.id}`}
                      onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                      className={`w-full text-left border px-1.5 py-1 text-[10px] leading-tight truncate hover:brightness-125 ${STATUS_CHIP[a.status] || "border-zinc-700 text-zinc-400"}`}
                      title={`${a.preferred_time} ${a.user_name} — ${titleFor(a)}`}
                    >
                      <span className="font-mono">{a.preferred_time}</span> {a.user_name}
                    </button>
                  ))}
                  {list.length > 3 && (
                    <button
                      data-testid={`mcal-more-${iso}`}
                      onClick={(e) => { e.stopPropagation(); setDayModal(iso); }}
                      className="w-full text-center text-[10px] font-mono text-zinc-500 hover:text-brand-red"
                    >
                      +{list.length - 3} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {dayModal && (
        <Modal onClose={() => setDayModal(null)} testId="mcal-day-modal">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">All bookings</div>
          <h4 className="mt-2 font-display text-2xl uppercase tracking-tight text-white">{dayModal}</h4>
          <div className="mt-4 space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {(byDay[dayModal] || []).map((a) => (
              <button key={a.id} onClick={() => { setSelected(a); setDayModal(null); }}
                className={`w-full text-left border px-3 py-2 text-xs hover:brightness-125 ${STATUS_CHIP[a.status] || "border-zinc-700 text-zinc-400"}`}>
                <span className="font-mono">{a.preferred_time}</span> · {a.user_name} — {titleFor(a)}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {selected && (
        <Modal onClose={() => setSelected(null)} testId="mcal-detail-modal">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Booking detail</div>
              <h4 className="mt-2 font-display text-2xl uppercase tracking-tight text-white">{selected.user_name}</h4>
            </div>
            <span className={`px-2.5 py-1 border text-[10px] font-mono uppercase tracking-widest ${STATUS_CHIP[selected.status]}`}>{STATUS_LABEL[selected.status]}</span>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <DetailRow label="When" value={`${selected.preferred_date} at ${selected.preferred_time}`} />
            <DetailRow label="Service" value={titleFor(selected)} />
            <DetailRow label="Vehicle" value={`${selected.vehicle_year || ""} ${selected.vehicle_make || ""} ${selected.vehicle_model || ""}${selected.vehicle_rego ? ` · ${selected.vehicle_rego}` : ""}`} />
            <DetailRow label="Email" value={selected.user_email} />
            {selected.user_phone && (
              <div className="flex gap-3">
                <span className="w-20 shrink-0 font-mono text-[10px] uppercase tracking-widest text-zinc-500 pt-0.5">Phone</span>
                <a href={`tel:${selected.user_phone}`} className="text-brand-red hover:underline inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{selected.user_phone}</a>
              </div>
            )}
            {selected.notes && <DetailRow label="Notes" value={selected.notes} />}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2 justify-between">
            <select
              data-testid="mcal-detail-status"
              value={selected.status}
              onChange={(e) => { onStatus(selected.id, e.target.value); setSelected({ ...selected, status: e.target.value }); }}
              className="bg-black border border-zinc-800 text-white text-xs font-mono uppercase tracking-widest px-2 py-2 focus:border-brand-red outline-none"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <button
              data-testid="mcal-detail-notify"
              onClick={() => { onNotify(selected.id); setSelected(null); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-red text-white font-mono text-[11px] uppercase tracking-widest hover:bg-[#B91C2B]"
            >
              <Send className="h-3.5 w-3.5" /> {selected.customer_notified_at ? "Re-send notify" : "Confirm & notify"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ children, onClose, testId }) => {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid={testId} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-6" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-1 text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        {children}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex gap-3">
    <span className="w-20 shrink-0 font-mono text-[10px] uppercase tracking-widest text-zinc-500 pt-0.5">{label}</span>
    <span className="text-zinc-200">{value}</span>
  </div>
);
